import { useCallback, useEffect, useRef, useState } from 'react';
import { LIBRARY_CHANGED } from '../artifacts/library-changed';
import {
  LibraryFeedUnavailable,
  readLibraryFeed,
  type LibraryFeedItem,
  type LibraryFeedPage,
  type LibraryGroup,
  type LibrarySegment,
} from './api';

const SEARCH_DEBOUNCE_MS = 300;

export type LibraryFeedState = {
  load: 'loading' | 'ready' | 'error' | 'unavailable';
  segment: LibrarySegment;
  group: LibraryGroup;
  query: string;
  items: LibraryFeedItem[];
  hasMore: boolean;
  appsMatchCount: number;
  degraded: boolean;
  setSegment: (segment: LibrarySegment) => void;
  setGroup: (group: LibraryGroup) => void;
  setQuery: (query: string) => void;
  loadMore: () => void;
  retry: () => void;
};

/**
 * The Library list's one controller. Authorization, filtering, search, counts and cursors are the server's: this holds the
 * person's controls, drops an answer that is no longer current, appends pages and re-reads after a save elsewhere. When the
 * server does not serve the feed, `load` is "unavailable" and the page keeps its legacy list.
 */
export function useLibraryFeed(
  initialGroup: LibraryGroup = 'all',
  /** False while the Library is not on screen (the page stays mounted behind Chat): nothing is read. */
  enabled = true,
  /** Changes when something saved elsewhere (a Chat save) should make the list read again. */
  refreshToken?: unknown,
): LibraryFeedState {
  const [segment, setSegment] = useState<LibrarySegment>('all');
  const [group, setGroup] = useState<LibraryGroup>(initialGroup);
  // A reused Library page (Chat stays mounted; Library and Files share it) is handed another starting group: follow it.
  const [wanted, setWanted] = useState<LibraryGroup>(initialGroup);
  if (wanted !== initialGroup) {
    setWanted(initialGroup);
    setGroup(initialGroup);
  }
  const [query, setQuery] = useState('');
  const [settled, setSettled] = useState('');
  const [load, setLoad] = useState<LibraryFeedState['load']>('loading');
  const [items, setItems] = useState<LibraryFeedItem[]>([]);
  const [next, setNext] = useState<string | null>(null);
  const [meta, setMeta] = useState<Pick<LibraryFeedPage, 'appsMatchCount' | 'search'>>({
    appsMatchCount: 0,
    search: null,
  });
  const [reload, setReload] = useState(0);
  const generation = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(query), query === '' ? 0 : SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const fetchPage = useCallback(
    async (cursor: string | null) => {
      const mine = ++generation.current;
      try {
        const page = await readLibraryFeed({ segment, group, query: settled, cursor });
        if (mine !== generation.current) return;
        setItems((current) => {
          const seen = new Set(cursor === null ? [] : current.map((item) => item.id));
          return [
            ...(cursor === null ? [] : current),
            ...page.items.filter((i) => !seen.has(i.id)),
          ];
        });
        setNext(page.nextCursor);
        setMeta({ appsMatchCount: page.appsMatchCount, search: page.search });
        setLoad('ready');
      } catch (error) {
        if (mine !== generation.current) return;
        if (error instanceof LibraryFeedUnavailable) return setLoad('unavailable');
        if (cursor === null) setItems([]);
        setLoad('error');
      }
    },
    [segment, group, settled],
  );

  // A save confirmed elsewhere re-reads the same list, keeping the rows on screen until the new answer lands.
  useEffect(() => {
    const changed = () => void fetchPage(null);
    window.addEventListener(LIBRARY_CHANGED, changed);
    return () => window.removeEventListener(LIBRARY_CHANGED, changed);
  }, [fetchPage]);

  useEffect(() => {
    if (!enabled) return;
    setLoad((was) => (was === 'unavailable' ? was : 'loading'));
    void fetchPage(null);
    return () => {
      generation.current++;
    };
  }, [fetchPage, reload, refreshToken, enabled]);

  return {
    load,
    segment,
    group,
    query,
    items,
    hasMore: next !== null,
    appsMatchCount: meta.appsMatchCount,
    degraded: meta.search?.degraded === true,
    setSegment,
    setGroup,
    setQuery,
    loadMore: () => {
      if (next !== null) void fetchPage(next);
    },
    retry: () => setReload((n) => n + 1),
  };
}
